/* eslint-disable arrow-body-style */
import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {concatLatestFrom} from "@ngrx/operators";
import {Store} from "@ngrx/store";
import {MatSnackBar} from "@angular/material/snack-bar";
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    exhaustMap,
    filter,
    fromEvent,
    map,
    mergeMap,
    of,
    startWith,
    switchMap,
    timer,
} from "rxjs";
import {tap} from "rxjs/operators";

import {ErrorHandlingUtil} from "../../shared/utils/error-handling-util";
import {AuthenticationService} from "../../shared/services/login/authentication.service";
import {selectSelectedSourceDataset} from "../../import/store/import.selectors";
import {NotificationApiService} from "../generated-sources/api/api/notification-api.service";
import {SubscriptionApiService} from "../generated-sources/api/api/subscription-api.service";
import {NotificationType, Subscription} from "../models/notification.model";
import {
    NotificationActions,
    NotificationCountsActions,
    NotificationListActions,
    SubscriptionActions,
} from "./notification.actions";
import {selectHasTrackedJobs, selectTrackedJobIds} from "./notification.selectors";

const POLL_INTERVAL_FAST_MS = 5_000;
const POLL_INTERVAL_SLOW_MS = 60_000;

/**
 * Adaptive counts polling.
 *  – Fires immediately on subscription, then every POLL_INTERVAL_*_MS.
 *  – Pauses when the tab is hidden; resumes on visibility change.
 *  – Pauses while the user is not authenticated.
 *  – Switches cadence between fast (5 s) and slow (60 s) based on whether at least
 *    one tracked job is in flight.
 */
export const pollCountsTimer = createEffect(
    (
        store$ = inject(Store),
        authService = inject(AuthenticationService)
    ) => {
        const visibility$ = fromEvent(document, "visibilitychange").pipe(
            map(() => document.visibilityState === "visible"),
            startWith(document.visibilityState === "visible"),
            distinctUntilChanged()
        );

        return combineLatest([
            store$.select(selectHasTrackedJobs).pipe(distinctUntilChanged()),
            visibility$,
            authService.isAuthenticated$.pipe(distinctUntilChanged()),
        ]).pipe(
            switchMap(([hasTrackedJobs, visible, authenticated]) => {
                if (!visible || !authenticated) {
                    return EMPTY;
                }
                const period = hasTrackedJobs ? POLL_INTERVAL_FAST_MS : POLL_INTERVAL_SLOW_MS;
                return timer(0, period);
            }),
            map(() => NotificationCountsActions.load())
        );
    },
    {functional: true}
);

/** Reload counts + subscriptions when the active dataset changes (skip the initial emit). */
export const reloadOnDatasetChange = createEffect(
    (store$ = inject(Store)) => {
        return store$.select(selectSelectedSourceDataset).pipe(
            distinctUntilChanged(),
            // The selector emits its initial value on subscription; we only want subsequent
            // changes since pollCountsTimer already covers the first fetch.
            mergeMap((dataset, index) =>
                index === 0 ? EMPTY : of(dataset)
            ),
            mergeMap(() => of(
                NotificationCountsActions.load(),
                SubscriptionActions.load()
            ))
        );
    },
    {functional: true}
);

export const loadCounts = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(NotificationApiService)
    ) => {
        return actions$.pipe(
            ofType(NotificationCountsActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([, dataset]) =>
                api.getNotificationCounts(dataset).pipe(
                    map((counts) => NotificationCountsActions.loadSuccess({counts})),
                    catchError((err: any) =>
                        of(NotificationCountsActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

/**
 * Panel-open defaults to the unread-only view per the Variant 1 UI: users see what
 * still needs their attention first, and can opt into the read history via the
 * "Show read (N)" footer link.
 */
export const loadListOnPanelOpen = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(NotificationListActions.panelOpened),
            map(() => NotificationListActions.load({unreadOnly: true}))
        );
    },
    {functional: true}
);

/** Footer-link toggle effects — flip the visible set by re-fetching with the right scope. */
export const loadListOnShowRead = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(NotificationListActions.showRead),
            map(() => NotificationListActions.load({unreadOnly: false}))
        );
    },
    {functional: true}
);

export const loadListOnHideRead = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(NotificationListActions.hideRead),
            map(() => NotificationListActions.load({unreadOnly: true}))
        );
    },
    {functional: true}
);

export const loadList = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(NotificationApiService)
    ) => {
        return actions$.pipe(
            ofType(NotificationListActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            switchMap(([action, dataset]) =>
                api.getNotifications(dataset, action.unreadOnly, 50, 0).pipe(
                    map((page) => NotificationListActions.loadSuccess({items: page.items, total: page.total})),
                    catchError((err: any) =>
                        of(NotificationListActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const markRead = createEffect(
    (actions$ = inject(Actions), api = inject(NotificationApiService)) => {
        return actions$.pipe(
            ofType(NotificationActions.markRead),
            mergeMap(({id}) =>
                api.markNotificationRead(id).pipe(
                    map(() => NotificationActions.markReadSuccess({id})),
                    catchError((err: any) =>
                        of(NotificationActions.markReadError({id, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const markAllRead = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(NotificationApiService)
    ) => {
        return actions$.pipe(
            ofType(NotificationActions.markAllRead),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([, dataset]) =>
                api.markAllNotificationsRead(dataset).pipe(
                    map(() => NotificationActions.markAllReadSuccess()),
                    catchError((err: any) =>
                        of(NotificationActions.markAllReadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

/**
 * Bulk-mark-read scoped by deepLink prefix — dispatched by pages that "own" a family of
 * notifications (currently /import for scrape-extraction events). Reuses the read-all endpoint
 * with its optional `deepLinkPrefix` query param. No snackbar on error: this fires passively
 * from navigation; a UI toast for a background-sweep failure would be more confusing than
 * useful. The next counts poll repairs any drift.
 */
export const markReadByDeepLinkPrefix = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(NotificationApiService)
    ) => {
        return actions$.pipe(
            ofType(NotificationActions.markReadByDeepLinkPrefix),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{deepLinkPrefix}, dataset]) =>
                api.markAllNotificationsRead(dataset, deepLinkPrefix).pipe(
                    map(() => NotificationActions.markReadByDeepLinkPrefixSuccess({deepLinkPrefix})),
                    catchError((err: any) =>
                        of(NotificationActions.markReadByDeepLinkPrefixError({
                            deepLinkPrefix,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(err),
                        }))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const dismiss = createEffect(
    (actions$ = inject(Actions), api = inject(NotificationApiService)) => {
        return actions$.pipe(
            ofType(NotificationActions.dismiss),
            mergeMap(({id}) =>
                api.dismissNotification(id).pipe(
                    map(() => NotificationActions.dismissSuccess({id})),
                    catchError((err: any) =>
                        of(NotificationActions.dismissError({id, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const showMutationError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(
                NotificationActions.markReadError,
                NotificationActions.markAllReadError,
                NotificationActions.dismissError,
                SubscriptionActions.subscribeError,
                SubscriptionActions.unsubscribeError
            ),
            tap(({errorMsg}) => snackBar.open(errorMsg || "Action failed.", "", {duration: 3000}))
        );
    },
    {functional: true, dispatch: false}
);

// ---------- subscriptions ----------

export const loadSubscriptions = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(SubscriptionApiService)
    ) => {
        return actions$.pipe(
            ofType(SubscriptionActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([, dataset]) =>
                api.getSubscriptions(dataset).pipe(
                    map((subs) => SubscriptionActions.loadSuccess({subscriptions: subs})),
                    catchError((err: any) =>
                        of(SubscriptionActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const subscribe = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(SubscriptionApiService)
    ) => {
        return actions$.pipe(
            ofType(SubscriptionActions.subscribe),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{targetType, targetId}, dataset]) =>
                api.subscribe({targetType, targetId}, dataset).pipe(
                    // The backend returns 204 — synthesise the persisted Subscription locally.
                    // The next subscriptionsLoad will canonicalise the row's id; for now we
                    // give it a non-(-1) id so the optimistic placeholder is replaced.
                    map(() => {
                        const synthesised: Subscription = {
                            id: 0,
                            targetType,
                            targetId,
                            subscribedAt: new Date().toISOString(),
                        };
                        return SubscriptionActions.subscribeSuccess({targetType, targetId, subscription: synthesised});
                    }),
                    catchError((err: any) =>
                        of(SubscriptionActions.subscribeError({
                            targetType,
                            targetId,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(err),
                        }))
                    )
                )
            )
        );
    },
    {functional: true}
);

/** After a real subscribe round-trip succeeds we ask the backend for the canonical row. */
export const reloadSubscriptionsAfterSuccess = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(SubscriptionActions.subscribeSuccess),
            map(() => SubscriptionActions.load())
        );
    },
    {functional: true}
);

export const unsubscribe = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(SubscriptionApiService)
    ) => {
        return actions$.pipe(
            ofType(SubscriptionActions.unsubscribe),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{targetType, targetId}, dataset]) =>
                api.unsubscribe(targetType, targetId, dataset).pipe(
                    map(() => SubscriptionActions.unsubscribeSuccess({targetType, targetId})),
                    catchError((err: any) =>
                        of(SubscriptionActions.unsubscribeError({
                            targetType,
                            targetId,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(err),
                        }))
                    )
                )
            )
        );
    },
    {functional: true}
);

/**
 * When the panel list arrives, untrack any tracked job whose JobFinished notification is now
 * visible — that returns counts polling to the slow cadence (60 s).
 */
export const untrackOnNotificationArrival = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store)
    ) => {
        return actions$.pipe(
            ofType(NotificationListActions.loadSuccess),
            concatLatestFrom(() => store$.select(selectTrackedJobIds)),
            filter(([, trackedIds]) => trackedIds.length > 0),
            mergeMap(([{items}, trackedIds]) => {
                const arrivedJobIds = items
                    .filter((n) => n.type === NotificationType.JobFinished && n.sourceJobId !== undefined)
                    .map((n) => n.sourceJobId!);
                const toUntrack = trackedIds.filter((id) => arrivedJobIds.includes(id));
                return of(...toUntrack.map((id) => NotificationActions.untrackJob({taskInfoId: id})));
            })
        );
    },
    {functional: true}
);

